import { useMemo } from 'react';
import { useUserLogin } from './useUserLogin';
import { useTipoUsuario } from './useTipoUsuario';
import {
  REVISION_STATUS_ID,
  EN_ACTUALIZACION_STATUS_ID,
  PRE_APROBADA_STATUS_ID,
  OBSERVADA_STATUS_ID,
  APROBADA_STATUS_ID,
  EN_PROCESO_STATUS_ID,
  INICIO_STATUS_ID,
  FINALIZADA_STATUS_ID,
  APROBADA_RT_STATUS_ID,
} from '../constants/constants';

interface SolicitudPermissionsConfig {
  solicitudEstadoId: number;
  tipoUsuarioCreacion?: string;
  existeCalificacion?: boolean;
}

export const useSolicitudPermissions = (
  config: SolicitudPermissionsConfig
) => {
  const { isContratistaTipoUsuario, isEmpresaGestoraTipoUsuario } =
    useUserLogin();
  const { esAnalistaSeguridad, esResponsableSeguridad } =
    useTipoUsuario();

  const isEmpresaGestora = isEmpresaGestoraTipoUsuario();
  const isContratista = isContratistaTipoUsuario();
  const { solicitudEstadoId, existeCalificacion } = config;

  // Funciones helper para evitar repetición de código
  const isAnalistaSecurityWithAprobadaRT = useMemo(
    () =>
      isEmpresaGestora &&
      esAnalistaSeguridad() &&
      solicitudEstadoId === APROBADA_RT_STATUS_ID,
    [isEmpresaGestora, esAnalistaSeguridad, solicitudEstadoId]
  );

  const isResponsableSecurityWithPreAprobada = useMemo(
    () =>
      isEmpresaGestora &&
      esResponsableSeguridad() &&
      solicitudEstadoId === PRE_APROBADA_STATUS_ID,
    [isEmpresaGestora, esResponsableSeguridad, solicitudEstadoId]
  );

  // CU55: el Analista de Seguridad también edita AR/PE en PRE_APROBADA (mismo criterio que el comentario)
  const isAnalistaSecurityWithPreAprobada = useMemo(
    () =>
      isEmpresaGestora &&
      esAnalistaSeguridad() &&
      solicitudEstadoId === PRE_APROBADA_STATUS_ID,
    [isEmpresaGestora, esAnalistaSeguridad, solicitudEstadoId]
  );

  const isEmpresaGestoraWithRevision = useMemo(
    () =>
      isEmpresaGestora && solicitudEstadoId === REVISION_STATUS_ID,
    [isEmpresaGestora, solicitudEstadoId]
  );

  // Estados comunes agrupados
  const commonRestrictedStates = useMemo(
    () => [
      REVISION_STATUS_ID,
      APROBADA_STATUS_ID,
      PRE_APROBADA_STATUS_ID,
      APROBADA_RT_STATUS_ID,
      EN_ACTUALIZACION_STATUS_ID,
      OBSERVADA_STATUS_ID,
    ],
    []
  );

  const empresaGestoraEditableStates = useMemo(
    () => [
      EN_PROCESO_STATUS_ID,
      INICIO_STATUS_ID,
      REVISION_STATUS_ID,
      APROBADA_STATUS_ID,
      EN_ACTUALIZACION_STATUS_ID,
    ],
    []
  );

  const contratistaEditableStates = useMemo(
    () => [
      EN_PROCESO_STATUS_ID,
      OBSERVADA_STATUS_ID,
      APROBADA_STATUS_ID,
      EN_ACTUALIZACION_STATUS_ID,
    ],
    []
  );

  const contratistaHistoryStates = useMemo(
    () => [
      OBSERVADA_STATUS_ID,
      EN_ACTUALIZACION_STATUS_ID,
      APROBADA_STATUS_ID,
    ],
    []
  );

  // 1. Lógica de formularios deshabilitados (patrón más común)
  // Datos Generales
  const isFormDisabled = useMemo(() => {
    // Empresa Gestora disabled states
    if (isEmpresaGestora) {
      const standardDisabledStates = [
        EN_PROCESO_STATUS_ID,
        REVISION_STATUS_ID,
        EN_ACTUALIZACION_STATUS_ID,
        PRE_APROBADA_STATUS_ID,
        APROBADA_RT_STATUS_ID,
        APROBADA_STATUS_ID,
        OBSERVADA_STATUS_ID,
      ];
      if (standardDisabledStates.includes(solicitudEstadoId)) {
        return true;
      }

      // Usar helpers para evitar repetición
      if (
        isAnalistaSecurityWithAprobadaRT ||
        isResponsableSecurityWithPreAprobada
      ) {
        return true;
      }
    }

    // Contratista disabled states
    if (
      isContratista &&
      (solicitudEstadoId === REVISION_STATUS_ID ||
        solicitudEstadoId === APROBADA_RT_STATUS_ID ||
        solicitudEstadoId === PRE_APROBADA_STATUS_ID)
    ) {
      return true;
    }

    return false;
  }, [
    isEmpresaGestora,
    isContratista,
    solicitudEstadoId,
    isAnalistaSecurityWithAprobadaRT,
    isResponsableSecurityWithPreAprobada,
  ]);

  // 2. Visibilidad de columnas de status documento
  const shouldShowDocumentStatusColumn = useMemo(() => {
    return (
      (isContratista && solicitudEstadoId === OBSERVADA_STATUS_ID) ||
      isEmpresaGestoraWithRevision ||
      isAnalistaSecurityWithAprobadaRT ||
      isResponsableSecurityWithPreAprobada
    );
  }, [
    isContratista,
    solicitudEstadoId,
    isEmpresaGestoraWithRevision,
    isAnalistaSecurityWithAprobadaRT,
    isResponsableSecurityWithPreAprobada,
  ]);

  // 3. Checkbox de status documento habilitado
  const isDocumentStatusCheckEnabled = useMemo(() => {
    return (
      isEmpresaGestoraWithRevision ||
      isAnalistaSecurityWithAprobadaRT ||
      isResponsableSecurityWithPreAprobada
    );
  }, [
    isEmpresaGestoraWithRevision,
    isAnalistaSecurityWithAprobadaRT,
    isResponsableSecurityWithPreAprobada,
  ]);

  // 4. Puede editar solicitud
  const canEditSolicitud = useMemo(() => {
    if (isEmpresaGestora) {
      return empresaGestoraEditableStates.includes(solicitudEstadoId);
    }

    if (isContratista) {
      return contratistaEditableStates.includes(solicitudEstadoId);
    }

    return false;
  }, [
    isEmpresaGestora,
    isContratista,
    solicitudEstadoId,
    empresaGestoraEditableStates,
    contratistaEditableStates,
  ]);

  // 5. Puede eliminar elementos (botón eliminar en tablas)
  const canDelete = useMemo(() => {
    return !commonRestrictedStates.includes(solicitudEstadoId);
  }, [solicitudEstadoId, commonRestrictedStates]);

  // 6. Puede mostrar observaciones
  const canShowObservations = useMemo(() => {
    return (
      isEmpresaGestoraWithRevision ||
      isAnalistaSecurityWithAprobadaRT ||
      isResponsableSecurityWithPreAprobada
    );
  }, [
    isEmpresaGestoraWithRevision,
    isAnalistaSecurityWithAprobadaRT,
    isResponsableSecurityWithPreAprobada,
  ]);

  // 7. Puede mostrar historial
  const canShowHistory = useMemo(() => {
    return (
      isEmpresaGestoraWithRevision ||
      isAnalistaSecurityWithAprobadaRT ||
      isResponsableSecurityWithPreAprobada ||
      (isContratista &&
        contratistaHistoryStates.includes(solicitudEstadoId))
    );
  }, [
    isEmpresaGestoraWithRevision,
    isAnalistaSecurityWithAprobadaRT,
    isResponsableSecurityWithPreAprobada,
    isContratista,
    contratistaHistoryStates,
    solicitudEstadoId,
  ]);

  // 8. Puede editar (específico para botones de editar en tablas)
  const canEdit = useMemo(() => {
    return (
      (isEmpresaGestora &&
        // solicitudEstadoId === APROBADA_STATUS_ID ||
        solicitudEstadoId === INICIO_STATUS_ID) ||
      isEmpresaGestoraWithRevision ||
      isAnalistaSecurityWithAprobadaRT ||
      isResponsableSecurityWithPreAprobada ||
      (isContratista &&
        contratistaEditableStates.includes(solicitudEstadoId))
    );
  }, [
    isEmpresaGestoraWithRevision,
    isAnalistaSecurityWithAprobadaRT,
    isResponsableSecurityWithPreAprobada,
    isContratista,
    isEmpresaGestora,
    contratistaEditableStates,
    solicitudEstadoId,
  ]);

  const canEditPlan = useMemo(() => {
    return (
      (isEmpresaGestora &&
        // solicitudEstadoId === APROBADA_STATUS_ID ||
        solicitudEstadoId === INICIO_STATUS_ID) ||
      isEmpresaGestoraWithRevision ||
      isAnalistaSecurityWithAprobadaRT ||
      isAnalistaSecurityWithPreAprobada ||
      isResponsableSecurityWithPreAprobada ||
      // CU55: el Contratista edita el PE en EN_PROCESO/OBSERVADA/APROBADA/ACTUALIZACION
      (isContratista &&
        contratistaEditableStates.includes(solicitudEstadoId))
    );
  }, [
    isEmpresaGestoraWithRevision,
    isAnalistaSecurityWithAprobadaRT,
    isAnalistaSecurityWithPreAprobada,
    isResponsableSecurityWithPreAprobada,
    isContratista,
    isEmpresaGestora,
    contratistaEditableStates,
    solicitudEstadoId,
  ]);

  // 8b. Puede editar (lógica completa de TablaSolicitud con perfiles)
  const canEditWithProfiles = useMemo(() => {
    const estadosEditablesEmpresaGestora = [
      INICIO_STATUS_ID,
      REVISION_STATUS_ID,
      APROBADA_STATUS_ID,
    ];

    const estadosEditablesContratista = [
      EN_PROCESO_STATUS_ID,
      OBSERVADA_STATUS_ID,
      APROBADA_STATUS_ID,
      EN_ACTUALIZACION_STATUS_ID,
    ];

    const estadosEditablesAnalista = [
      INICIO_STATUS_ID,
      REVISION_STATUS_ID,
      APROBADA_STATUS_ID,
      APROBADA_RT_STATUS_ID,
      PRE_APROBADA_STATUS_ID,
    ];

    const estadosEditablesResponsable = [
      INICIO_STATUS_ID,
      REVISION_STATUS_ID,
      APROBADA_STATUS_ID,
      PRE_APROBADA_STATUS_ID,
    ];

    const puedeEditarEmpresaGestora =
      isEmpresaGestora &&
      estadosEditablesEmpresaGestora.includes(solicitudEstadoId) &&
      !esAnalistaSeguridad() &&
      !esResponsableSeguridad();

    const puedeEditarContratista =
      isContratista &&
      estadosEditablesContratista.includes(solicitudEstadoId);

    const puedeEditarAnalistaSeguridad =
      isEmpresaGestora &&
      estadosEditablesAnalista.includes(solicitudEstadoId) &&
      esAnalistaSeguridad();

    const puedeEditarResponsableSeguridad =
      isEmpresaGestora &&
      estadosEditablesResponsable.includes(solicitudEstadoId) &&
      esResponsableSeguridad();

    return (
      puedeEditarEmpresaGestora ||
      puedeEditarContratista ||
      puedeEditarAnalistaSeguridad ||
      puedeEditarResponsableSeguridad
    );
  }, [
    isEmpresaGestora,
    isContratista,
    solicitudEstadoId,
    esAnalistaSeguridad,
    esResponsableSeguridad,
  ]);

  // 9. Lógica de botones de Componente Solicitud
  const actionPermissions = useMemo(() => {
    const baseDisabled = solicitudEstadoId === FINALIZADA_STATUS_ID;

    // Estados para diferentes acciones
    const anulateStatesEmpresa = [
      //REVISION_STATUS_ID,
      INICIO_STATUS_ID,
      // EN_PROCESO_STATUS_ID,
      // APROBADA_STATUS_ID,
      OBSERVADA_STATUS_ID,
    ];

    const anulateStatesContratista = [
      // OBSERVADA_STATUS_ID,
      // APROBADA_STATUS_ID,
      //EN_ACTUALIZACION_STATUS_ID,
      REVISION_STATUS_ID,
      EN_PROCESO_STATUS_ID,
    ];

    const qualifyStates = [
      REVISION_STATUS_ID,
      PRE_APROBADA_STATUS_ID,
      APROBADA_RT_STATUS_ID,
    ];

    const sendStatesEmpresa = existeCalificacion
      ? [
          REVISION_STATUS_ID,
          PRE_APROBADA_STATUS_ID,
          APROBADA_RT_STATUS_ID,
          OBSERVADA_STATUS_ID,
          EN_PROCESO_STATUS_ID,
          APROBADA_STATUS_ID,
          EN_ACTUALIZACION_STATUS_ID,
        ]
      : [
          OBSERVADA_STATUS_ID,
          // EN_PROCESO_STATUS_ID,
          //REVISION_STATUS_ID,
          // APROBADA_STATUS_ID,
          EN_ACTUALIZACION_STATUS_ID,
          //PRE_APROBADA_STATUS_ID,
          // APROBADA_RT_STATUS_ID,
          INICIO_STATUS_ID,
        ];
    const sendStatesContratista = [
      REVISION_STATUS_ID,
      EN_PROCESO_STATUS_ID,
      EN_ACTUALIZACION_STATUS_ID,
      OBSERVADA_STATUS_ID,
      //APROBADA_STATUS_ID,
    ];
    const finalizeBlockedEmpresa = [
      INICIO_STATUS_ID,
      EN_PROCESO_STATUS_ID,
      FINALIZADA_STATUS_ID,
      EN_ACTUALIZACION_STATUS_ID,
      REVISION_STATUS_ID,
      OBSERVADA_STATUS_ID,
      PRE_APROBADA_STATUS_ID,
      APROBADA_RT_STATUS_ID,
    ];

    const finalizeBlockedContratista = [
      EN_PROCESO_STATUS_ID,
      OBSERVADA_STATUS_ID,
      APROBADA_STATUS_ID,
      EN_ACTUALIZACION_STATUS_ID,
      REVISION_STATUS_ID,
    ];

    return {
      canAnulate:
        !baseDisabled &&
        ((isEmpresaGestora &&
          anulateStatesEmpresa.includes(solicitudEstadoId)) ||
          (isContratista &&
            anulateStatesContratista.includes(solicitudEstadoId))),

      canSend:
        !baseDisabled &&
        ((isEmpresaGestora &&
          sendStatesEmpresa.includes(solicitudEstadoId)) ||
          (isContratista &&
            sendStatesContratista.includes(solicitudEstadoId))),

      canQualify:
        isEmpresaGestora && qualifyStates.includes(solicitudEstadoId),

      canFinalize:
        !baseDisabled &&
        ((isEmpresaGestora &&
          !finalizeBlockedEmpresa.includes(solicitudEstadoId)) ||
          (isContratista &&
            !finalizeBlockedContratista.includes(solicitudEstadoId))),
    };
  }, [
    isEmpresaGestora,
    isContratista,
    solicitudEstadoId,
    existeCalificacion,
  ]);

  // 10. Para mostrar columnas de observaciones en contratistas
  const shouldShowObservationColumn = useMemo(() => {
    return isContratista && solicitudEstadoId === OBSERVADA_STATUS_ID;
  }, [isContratista, solicitudEstadoId]);

  // 11. Puede editar observaciones (específico para ObservacionComponent)
  const canEditObservations = useMemo(() => {
    return (
      isEmpresaGestoraWithRevision ||
      isAnalistaSecurityWithAprobadaRT ||
      isResponsableSecurityWithPreAprobada
    );
  }, [
    isEmpresaGestoraWithRevision,
    isAnalistaSecurityWithAprobadaRT,
    isResponsableSecurityWithPreAprobada,
  ]);

  // 12. Disabled state para campos de formulario con lógica de edición
  const isFormFieldDisabled = useMemo(() => {
    return (
      solicitudEstadoId === REVISION_STATUS_ID ||
      solicitudEstadoId === APROBADA_STATUS_ID ||
      solicitudEstadoId === EN_ACTUALIZACION_STATUS_ID
    );
  }, [solicitudEstadoId]);

  // 13. Disabled state para campos de formulario en modo edición (incluye ID check)
  const isFormFieldDisabledInEditMode = useMemo(() => {
    return (id: number) => {
      return (
        solicitudEstadoId === REVISION_STATUS_ID ||
        solicitudEstadoId === PRE_APROBADA_STATUS_ID ||
        solicitudEstadoId === APROBADA_RT_STATUS_ID ||
        (id !== 0 && isFormFieldDisabled)
      );
    };
  }, [solicitudEstadoId, isFormFieldDisabled]);

  // 14. Estados donde se puede mostrar la sección de documentos
  const canShowDocumentSection = useMemo(() => {
    const documentSectionStates = [
      EN_PROCESO_STATUS_ID,
      OBSERVADA_STATUS_ID,
      REVISION_STATUS_ID,
      APROBADA_STATUS_ID,
      PRE_APROBADA_STATUS_ID,
      EN_ACTUALIZACION_STATUS_ID,
      APROBADA_RT_STATUS_ID,
    ];
    return documentSectionStates.includes(solicitudEstadoId);
  }, [solicitudEstadoId]);

  return {
    // Estados básicos
    isEmpresaGestora,
    isContratista,
    esAnalistaSeguridad: esAnalistaSeguridad(),

    // Helper variables para casos específicos
    isAnalistaSecurityWithAprobadaRT,
    isResponsableSecurityWithPreAprobada,
    isEmpresaGestoraWithRevision,

    // Permisos principales
    isFormDisabled,
    canEditSolicitud,
    canDelete,
    canEdit,
    canEditWithProfiles,
    canEditPlan,

    // Permisos de UI
    shouldShowDocumentStatusColumn,
    isDocumentStatusCheckEnabled,
    canShowObservations,
    canShowHistory,
    shouldShowObservationColumn,
    canEditObservations,
    isFormFieldDisabled,
    isFormFieldDisabledInEditMode,
    canShowDocumentSection,

    // Permisos de acciones
    actionPermissions,

    // Utilidades para casos específicos
    checkCustomCondition: (condition: () => boolean) => condition(),
  };
};
